import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { X } from 'lucide-react';
import PropTypes from 'prop-types';

import { cn } from '@/shadcn/lib/utils';

import { uploadConfig } from '@/client/media';
import { FormWriter } from '@/components/forms/components/form/writer';
import {
  FORMS_BATCH_DELETE,
  FORMS_BATCH_REPORT_VALIDATION_STATUS,
  FORMS_BATCH_TOGGLE_LINK,
  FORMS_BATCH_TOGGLE_LOCK,
  FORMS_BATCH_UPDATE
} from '../reducers';

export const BatchForm = memo(
  ({
    workflowContext,
    schema,
    uiSchema,
    id,
    isMainForm = false,
    isInBatch = false,
    data,
    linkedPaths = [],
    lockedPaths = [],
    updateForms,
    uploadFile,
    validating,
    hasErrorFocus
  }) => {
    // callback to close a form
    const onFormClose = useCallback(() => {
      updateForms({
        type: FORMS_BATCH_DELETE,
        id
      });
    }, [id, updateForms]);

    // callback to update form data
    const onFormDataChange = useCallback(
      args => {
        const { formData } = args;
        updateForms({
          type: FORMS_BATCH_UPDATE,
          id,
          data: formData
        });
      },
      [id, updateForms]
    );

    // ref to a hidden submit button, to trigger form validation manually
    const sumbitBtnRef = useRef(null);
    useEffect(() => {
      // if validating state changes and is now true, trigger form validation manually
      // validation result will be reported thanks to reportSuccess and reportError
      if (validating && sumbitBtnRef.current) sumbitBtnRef.current.click();
    }, [validating]);
    const reportSuccess = useCallback(() => {
      updateForms({
        type: FORMS_BATCH_REPORT_VALIDATION_STATUS,
        id,
        success: true
      });
    }, [id, updateForms]);
    const reportError = useCallback(() => {
      updateForms({
        type: FORMS_BATCH_REPORT_VALIDATION_STATUS,
        id,
        success: false
      });
    }, [id, updateForms]);

    const links = useMemo(
      () => ({
        id,
        isMainForm,
        onClickLock: path => {
          updateForms({
            type: FORMS_BATCH_TOGGLE_LOCK,
            path
          });
        },
        onClickLink: path => {
          updateForms({
            type: FORMS_BATCH_TOGGLE_LINK,
            id,
            path
          });
        },
        linkedPaths,
        lockedPaths
      }),
      [id, isMainForm, linkedPaths, lockedPaths, updateForms]
    );

    const staticUploadConfig = useMemo(
      () => ({
        maxFileSize: uploadConfig.MAX_FILE_SIZE,
        fileExtensionWhitelistMap: uploadConfig.FILE_EXTENSION_WHITELIST_MAP
      }),
      []
    );

    return (
      <div
        className={cn(
          'relative mr-5 w-[520px] shrink-0 rounded border border-border bg-card p-4',
          isMainForm && 'sticky left-0 z-2 shadow-xs',
          hasErrorFocus && 'border-destructive'
        )}
        data-error-focus={hasErrorFocus}
        data-is-main={isMainForm}
      >
        <div className="relative flex h-6 w-full flex-row flex-nowrap items-center justify-end text-muted-foreground text-xs">
          {isInBatch &&
            (isMainForm ? (
              'Main form'
            ) : (
              <div
                className="-right-1 absolute cursor-pointer text-muted-foreground hover:text-destructive"
                data-cy="close-form"
                onClick={onFormClose}
              >
                <X size={20} />
              </div>
            ))}
        </div>
        <FormWriter
          formData={data}
          id={id}
          links={links}
          onChange={onFormDataChange}
          onError={reportError}
          onSubmit={reportSuccess}
          schema={schema}
          uiSchema={uiSchema}
          uploadConfig={staticUploadConfig}
          uploadFile={uploadFile}
          workflowContext={workflowContext}
        />
        <button className="hidden" form={id} ref={sumbitBtnRef} type="submit" />
      </div>
    );
  }
);
BatchForm.propTypes = {
  workflowContext: PropTypes.object.isRequired,
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  isMainForm: PropTypes.bool,
  isInBatch: PropTypes.bool,
  data: PropTypes.object.isRequired,
  linkedPaths: PropTypes.arrayOf(PropTypes.string),
  lockedPaths: PropTypes.arrayOf(PropTypes.string),
  updateForms: PropTypes.func.isRequired,
  uploadFile: PropTypes.func.isRequired,
  validating: PropTypes.bool.isRequired,
  hasErrorFocus: PropTypes.bool.isRequired
};
